import { Fragment, h } from "preact";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "preact/hooks";
import { useBlocker, useNavigate } from "react-router-dom";

import i18n from "../../i18n";
import { createFormData } from "../../services/forms";
import projectsApi from "../../api/projects";
import { lowerCaseKeys } from "../../helpers";
import { sendError } from "../../errors";
import {
  createToastMessage,
  selectCompany,
  selectCurrentUserId,
  setLoading,
} from "../../store";

import BackButton from "../BackButton.jsx";
import Button from "../Button.jsx";
import ProjectWizardStep1 from "./ProjectWizardStep1.jsx";
import ProjectWizardStep2 from "./ProjectWizardStep2.jsx";
import ProjectWizardStep3 from "./ProjectWizardStep3.jsx";
import WithConfirmation from "../WithConfirmation.jsx";
import UnsavedChangesPrompt from "../UnsavedChangesPrompt.jsx";

const FORM_STEP1 = {
  recruiters: {
    validations: ["requiredArray"],
  },
  jobTitle: {
    validations: ["required"],
  },
  areaOfWork: {
    validations: ["required"],
  },
  companyName: {
    validations: [],
  },
  locations: {
    validations: [],
  },
  workplaceType: {
    validations: ["required"],
  },
  employmentType: {
    validations: ["selectionRequired"],
  },
  salary: {
    validations: [],
  },
  languages: {
    validations: ["requiredSkillset"],
  },
};
const FORM_STEP2 = {
  seniority: {
    validations: [],
  },
  skills: {
    validations: ["requiredSkillset"],
  },
};
const FORM_STEP3 = {
  about: {
    validations: [],
  },
  benefits: {
    validations: [],
  },
  qualifications: {
    validations: [],
  },
  responsibilities: {
    validations: [],
  },
};

const MAX_STEPS = 3;

export default function ProjectWizard({ onBack, project }) {
  const currentUserId = useSelector(selectCurrentUserId);
  const currentCompany = useSelector(selectCompany);
  const navigate = useNavigate();

  const forms = {
    1: createFormData(
      FORM_STEP1,
      project || {
        locations: [],
        salary: [70000, 90000],
        companyName: currentCompany.name,
        recruiters: [currentUserId],
      }
    ),
    2: createFormData(FORM_STEP2, project || { seniority: 1 }),
    3: createFormData(FORM_STEP3, project || {}),
  };
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [configError, setConfigError] = useState(null);
  const [config, setConfig] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [done, setDone] = useState(false);

  const isEditMode = !!project;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !done &&
      currentLocation.pathname !== nextLocation.pathname &&
      (forms[1].changed || forms[2].changed || forms[3].changed)
  );

  useEffect(async () => {
    dispatch(setLoading(true));
    try {
      const config = await projectsApi.fetchCreateConfig();
      setConfig(config);
    } catch (err) {
      console.error(err);
      setConfigError(i18n.t("errors.unexpected_error"));
    }
    dispatch(setLoading(false));
  }, []);

  useEffect(() => {
    setButtonDisabled(!forms[step].teaseValidation());
  }, [forms, step]);

  async function handleSubmit() {
    const data = {
      ...forms[1].formData,
      ...forms[2].formData,
      ...forms[3].formData,
    };
    if (!isEditMode && step < MAX_STEPS) {
      setStep(step + 1);
      return;
    }
    setDone(true);
    try {
      if (isEditMode) {
        await projectsApi.update(project.id, lowerCaseKeys(data));
      } else {
        await projectsApi.create(lowerCaseKeys(data));
      }
      if (isEditMode) {
        createToastMessage(dispatch, {
          type: "success",
          message: i18n.t(
            "component.projects_add.projects_wizard.success_message.edit"
          ),
        });
        if (data.recruiters.indexOf(currentUserId) > -1) {
          navigate(`/projects/${project.id}`);
        } else {
          navigate(`/projects`);
          createToastMessage(dispatch, {
            type: "info",
            message: i18n.t(
              "component.projects_add.projects_wizard.unassigned_toast"
            ),
          });
        }
      } else {
        navigate(`/projects`);
        createToastMessage(dispatch, {
          type: "success",
          message: i18n.t(
            "component.projects_add.projects_wizard.success_message.new"
          ),
        });
        if (data.recruiters.indexOf(currentUserId) === -1) {
          createToastMessage(dispatch, {
            type: "info",
            message: i18n.t(
              "component.projects_add.projects_wizard.unassigned_toast"
            ),
          });
        }
      }
    } catch (err) {
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        sendError(err);
        createToastMessage(dispatch, {
          type: "error",
          message: i18n.t(
            "component.projects_add.projects_wizard.server_error"
          ),
        });
      }
    }
  }

  async function handleDeleteClick(event) {
    event.preventDefault();
    try {
      await projectsApi.deleteProject(project.id);

      navigate(`/projects`);
      createToastMessage(dispatch, {
        type: "info",
        message: i18n.t(
          "component.projects_add.projects_wizard.deletion_success_toast"
        ),
      });
    } catch (err) {
      console.error(err);
      createToastMessage(dispatch, {
        type: "error",
        message: i18n.t("errors.unexpected_error"),
      });
    }
  }

  function handleBackClick() {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onBack();
    }
  }

  if (!forms || configError) {
    return null;
  }

  if (!config) {
    return null;
  }

  return (
    <>
      <form
        method="post"
        action="#"
        onSubmit={forms[step].onSubmit(handleSubmit)}
      >
        {!isEditMode && (
          <p class="t-overline color-shades-charcoal">
            {i18n.t("component.projects_add.projects_wizard.step", {
              count: step,
              max: MAX_STEPS,
            })}
          </p>
        )}

        {(isEditMode || step == 1) && (
          <ProjectWizardStep1
            editMode={isEditMode}
            config={config}
            formErrors={formErrors}
            form={forms[1]}
          />
        )}
        {(isEditMode || step == 2) && (
          <ProjectWizardStep2
            editMode={isEditMode}
            config={config}
            formErrors={formErrors}
            form={forms[2]}
            formStep1={forms[1]}
          />
        )}
        {(isEditMode || step == 3) && (
          <ProjectWizardStep3
            editMode={isEditMode}
            config={config}
            formErrors={formErrors}
            form={forms[3]}
          />
        )}

        {isEditMode ? (
          <div>
            <WithConfirmation
              title={i18n.t(
                "component.projects_add.projects_wizard.delete_dialog.title"
              )}
              onSuccess={handleDeleteClick}
              trackingName="projectWizard.deleteDialog"
              okButton={i18n.t(
                "component.projects_add.projects_wizard.delete_dialog.success_button"
              )}
              okButtonDanger
              trigger={(clickHandler) => (
                <Button
                  danger
                  class="ml-8 mr-8"
                  trackingName={"projectWizard.delete"}
                  onClick={clickHandler}
                >
                  {i18n.t(
                    "component.projects_add.projects_wizard.edit.delete_button"
                  )}
                </Button>
              )}
            />
            <Button type="submit" trackingName={"projectWizard.submit"}>
              {i18n.t(
                "component.projects_add.projects_wizard.edit.submit_button"
              )}
            </Button>
          </div>
        ) : (
          <div>
            <BackButton
              class="mr-2"
              onClick={handleBackClick}
              trackingName="projectWizardStepBack"
              noIndent
            />
            <Button
              type="submit"
              trackingName={
                step == 3 ? "projectWizard.submit" : "projectWizard.nextStep"
              }
              disabled={buttonDisabled}
            >
              {i18n.t(step == 3 ? "actions.submit" : "actions.ok")}
            </Button>
          </div>
        )}
      </form>
      {blocker.state === "blocked" ? (
        <UnsavedChangesPrompt
          acceptButton={i18n.t(
            "component.projects_add.projects_wizard.unsaved_changes_prompt.ok"
          )}
          cancelButton={i18n.t(
            "component.projects_add.projects_wizard.unsaved_changes_prompt.cancel"
          )}
          text={i18n.t(
            "component.projects_add.projects_wizard.unsaved_changes_prompt.text"
          )}
          onAccept={() => blocker.proceed()}
          onCancel={() => blocker.reset()}
        />
      ) : null}
    </>
  );
}
